import React from 'react';
import { Phone, Mail, MapPin, ArrowUpRight, Clock } from "lucide-react";
import { createPageUrl } from "@/utils";
import SocialIcons from '@/components/plumbing/SocialIcons';

const services = [
  { label: 'Commercial Shopfit Plumbing', href: '/services' },
  { label: 'Retail & Hospitality Fitouts', href: '/industries/restaurants' },
  { label: 'New Commercial Builds', href: '/commercial-plumber-brisbane' },
  { label: 'Grease Trap Installation', href: '/grease-pit-plumbing-brisbane' },
  { label: 'Maintenance & Repairs', href: '/services' },
];

export default function Footer() {
  return (
    <footer style={{ backgroundColor: '#080d14' }} className="relative border-t border-[#2563eb]/70 w-full overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#2563eb] to-transparent" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_0.8fr_0.8fr_1fr] gap-12 lg:gap-14">
          <div>
            <img
              src="/images/optimized/performance/logo-280.webp"
              alt="Oliver & Sons Plumbing"
              width="280"
              height="280"
              className="w-[230px] md:w-[270px] h-auto mb-7"
            />
            <p className="text-white text-lg font-semibold mb-3">Commercial Plumbing Specialists</p>
            <p className="text-gray-400 text-base leading-7 max-w-sm">
              Family owned and operated, delivering compliant commercial plumbing across Brisbane and South East Queensland.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              <span className="rounded-full border border-[#2563eb]/30 bg-[#2563eb]/8 px-4 py-2 text-sm text-gray-300">
                QBCC Lic. 15538687
              </span>
              <span className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-gray-400">
                Fully Insured
              </span>
            </div>
          </div>

          <div>
            <h3 className="text-white text-sm font-semibold tracking-[0.2em] uppercase mb-7">Services</h3>
            <ul className="flex flex-col gap-3">
              {services.map((s) => (
                <li key={s.href + s.label}><a href={s.href} className="text-gray-300 text-base hover:text-white transition-colors">{s.label}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white text-sm font-semibold tracking-[0.2em] uppercase mb-7">Company</h3>
            <ul className="flex flex-col gap-3">
              <li>
                <a href="/projects" className="text-gray-300 text-base hover:text-white transition-colors inline-flex items-center gap-1">
                  Portfolio <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </li>
              <li>
                <a href="/services" className="text-gray-300 text-base hover:text-white transition-colors inline-flex items-center gap-1">
                  Services <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </li>
              <li>
                <a href="/service-areas" className="text-gray-300 text-base hover:text-white transition-colors inline-flex items-center gap-1">
                  Service Areas <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </li>
              <li>
                <a href="/#about" className="text-gray-300 text-base hover:text-white transition-colors inline-flex items-center gap-1">
                  About Us <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </li>
              <li>
                <a href="/industries" className="text-gray-300 text-base hover:text-white transition-colors inline-flex items-center gap-1">
                  Industries <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </li>
              <li>
                <a href="/privacy-policy" className="text-gray-300 text-base hover:text-white transition-colors inline-flex items-center gap-1">
                  Legal <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-white text-sm font-semibold tracking-[0.2em] uppercase mb-7">Get in Touch</h3>
            <div className="flex flex-col gap-4">
              <a
                href="tel:0403448370"
                className="flex items-center gap-3 text-gray-200 hover:text-white transition-colors text-base"
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-full border border-[#2563eb]/25 bg-[#2563eb]/8">
                  <Phone className="w-4 h-4 text-[#3b82f6]" />
                </span>
                <span>
                  <span className="block text-sm text-gray-400 mb-1">Phone</span>
                  0403 448 370
                </span>
              </a>

              <a
                href="mailto:info@oliverandsons.com.au"
                className="flex items-center gap-3 text-gray-200 hover:text-white transition-colors text-base"
              >
                <span className="flex items-center justify-center w-9 h-9 rounded-full border border-[#2563eb]/25 bg-[#2563eb]/8">
                  <Mail className="w-4 h-4 text-[#3b82f6]" />
                </span>
                <span>
                  <span className="block text-sm text-gray-400 mb-1">Email</span>
                  info@oliverandsons.com.au
                </span>
              </a>

              <div className="flex items-center gap-3 text-gray-300 text-base">
                <span className="flex items-center justify-center w-9 h-9 rounded-full border border-white/10 bg-white/[0.03]">
                  <MapPin className="w-4 h-4 text-[#3b82f6]" />
                </span>
                <span>
                  <span className="block text-sm text-gray-400 mb-1">Service Area</span>
                  Brisbane &amp; South East Queensland
                </span>
              </div>

              <div className="flex items-start gap-3 text-gray-300 text-base">
                <span className="flex items-center justify-center w-9 h-9 rounded-full border border-white/10 bg-white/[0.03] flex-shrink-0">
                  <Clock className="w-4 h-4 text-[#3b82f6]" />
                </span>
                <span>
                  <span className="block text-sm text-gray-400 mb-1">Business Hours</span>
                  <span className="block">Monday – Friday</span>
                  <span className="block">8:00am – 5:00pm</span>
                </span>
              </div>
            </div>

            <a
              href={createPageUrl('RequestAQuote')}
              className="inline-flex items-center justify-center mt-7 rounded-full bg-[#2563eb] hover:bg-[#1d4ed8] px-7 py-3.5 text-base font-semibold text-white transition-all shadow-[0_8px_24px_rgba(37,99,235,0.22)]"
            >
              Request a Quote <span className="ml-2">→</span>
            </a>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-white/7">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-5">
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-center lg:text-left">
              <p className="text-gray-400 text-sm">© {new Date().getFullYear()} Oliver &amp; Sons Plumbing. All rights reserved.</p>
              <span className="hidden sm:block h-3 w-px bg-white/10" />
              <p className="text-gray-400 text-sm">QBCC Lic. 15538687</p>
              <a href="/privacy-policy#privacy-policy" className="text-gray-400 hover:text-gray-300 text-sm transition-colors">Privacy Policy</a>
              <a href="/privacy-policy#terms-and-conditions" className="text-gray-400 hover:text-gray-300 text-sm transition-colors">Terms &amp; Conditions</a>
            </div>
            <SocialIcons />
          </div>
        </div>
      </div>
    </footer>
  );
}

